import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { fmtNumber } from "../../lib/utils";
import {
  ComposedChart, Bar, Line, ResponsiveContainer, CartesianGrid,
  XAxis, YAxis, Tooltip,
} from "recharts";
import {
  Info, FileBarChart2, Download, RefreshCcw, ChevronDown, ChevronUp,
} from "lucide-react";

const MODES = [
  { id: "interim", label: "Interim",
    blurb: "Includes all member tenants — figures revise as members close their period." },
  { id: "actual", label: "Actual",
    blurb: "Only tenants with ≥80% approved KPIs and confidence ≥0.8 are included." },
];

export default function IbpapConsolidated() {
  const [mode, setMode] = useState("interim");
  const [period, setPeriod] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [expandedKpi, setExpandedKpi] = useState(null);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/dashboard", { params: { mode, ...(period && { period }) } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [mode, period]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const r of data.rows) {
      (out[r.kpi.material_issue] = out[r.kpi.material_issue] || []).push(r);
    }
    return out;
  }, [data]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/platform/report", { mode, period: data?.period });
      setReport(r.data);
    } finally { setGenerating(false); }
  };

  const downloadReport = (rep) => {
    let md = `# ${rep.title}\n_${rep.mode.toUpperCase()} · Period ${rep.period} · generated ${rep.generated_at} by ${rep.generated_by}_\n\n`;
    for (const s of rep.sections) md += `## ${s.title}\n\n${s.body}\n\n`;
    const blob = new Blob([md], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ibpap_consolidated_${rep.mode}_${rep.period}.md`;
    a.click();
  };

  if (loading && !data) return <div className="p-6 text-ink-soft">Loading consolidated view…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="ibpap-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="section-label">Platform Admin · Consolidated</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">IBPAP Consolidated ESG Dashboard</h1>
          <p className="text-sm text-ink-soft max-w-3xl">
            Cross-tenant aggregation of every member's ESG KPIs. Switch between
            <span className="font-semibold"> Interim</span> (all members) and
            <span className="font-semibold"> Actual</span> (only members with complete data) to compare scope coverage.
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <input
            data-testid="period-input"
            placeholder="Period (e.g. 2025-Q4)"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-border rounded-md px-3 py-1.5 text-sm font-mono w-44"
          />
          <button data-testid="refresh" onClick={load} className="btn btn-ghost"><RefreshCcw size={12}/></button>
          <button
            data-testid="generate-report"
            onClick={generateReport}
            disabled={generating}
            className="btn btn-primary"
          >
            {generating ? "Generating…" : (<><FileBarChart2 size={12}/> Generate IBPAP Report</>)}
          </button>
        </div>
      </div>

      {/* Mode switch */}
      <div className="flex gap-2 flex-wrap" data-testid="mode-switcher">
        {MODES.map((m) => (
          <button
            key={m.id}
            data-testid={`mode-${m.id}`}
            onClick={() => setMode(m.id)}
            className={"card p-3 text-left flex-1 min-w-[260px] cursor-pointer transition " +
              (mode === m.id ? "border-brand ring-2 ring-brand/20" : "hover:border-brand-200")}
          >
            <div className="flex items-center justify-between">
              <span className="section-label">{m.label}</span>
              {mode === m.id && <span className="pill pill-success text-[10px]">Active</span>}
            </div>
            <div className="text-xs text-ink-soft mt-1">{m.blurb}</div>
          </button>
        ))}
      </div>

      {data && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-tiles">
            <SummaryTile label="Mode" value={data.mode.toUpperCase()} sub={`Period ${data.period}`} color="#064E3B"/>
            <SummaryTile label="Tenants included" value={data.summary.tenants_included} sub={`of ${data.summary.tenants_total}`} color="#10B981"/>
            <SummaryTile label="Tenants excluded" value={data.summary.tenants_excluded} sub="incomplete data" color="#EF4444"/>
            <SummaryTile label="KPIs aggregated" value={data.summary.kpis_aggregated} sub={`${data.summary.material_issues} material issues`} color="#3B82F6"/>
            <SummaryTile label="Generated" value={new Date(data.as_of).toLocaleString().slice(0, 17)} sub="UTC" color="#94A3B8"/>
          </div>

          {/* Tenant scope */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card p-4" data-testid="tenants-included">
              <div className="section-label">Tenants in scope ({data.tenants_included.length})</div>
              <table className="dense w-full mt-2">
                <thead><tr><th>Tenant</th><th>Country</th><th className="text-right">FTE</th><th className="text-right">Completeness</th></tr></thead>
                <tbody>
                  {data.tenants_included.map((t) => (
                    <tr key={t.id}>
                      <td className="font-medium">{t.name}</td>
                      <td className="text-[11px] text-ink-soft">{t.country}</td>
                      <td className="num">{fmtNumber(t.headcount, 0)}</td>
                      <td className="num">{t.completeness.completion_pct}%</td>
                    </tr>
                  ))}
                  {data.tenants_included.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-ink-soft py-4 text-xs">No tenants meet the completeness threshold in Actual mode.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="card p-4" data-testid="tenants-excluded">
              <div className="section-label">Tenants excluded ({data.tenants_excluded.length})</div>
              <div className="text-xs text-ink-soft mt-0.5">
                {mode === "interim" ? "None in Interim mode — all tenants are included." : "Below the 80% completeness threshold."}
              </div>
              <table className="dense w-full mt-2">
                <thead><tr><th>Tenant</th><th>Country</th><th className="text-right">Completeness</th><th className="text-right">Approved KPIs</th></tr></thead>
                <tbody>
                  {data.tenants_excluded.map((t) => (
                    <tr key={t.id}>
                      <td className="font-medium">{t.name}</td>
                      <td className="text-[11px] text-ink-soft">{t.country}</td>
                      <td className="num">{t.completeness.completion_pct}%</td>
                      <td className="num">{t.completeness.approved_kpis}/{t.completeness.expected_kpis}</td>
                    </tr>
                  ))}
                  {data.tenants_excluded.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-ink-soft py-4 text-xs">—</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Emissions trend (aggregated) */}
          <div className="card p-4" data-testid="emissions-trend">
            <div className="flex items-center justify-between">
              <div>
                <div className="section-label">Aggregate GHG Emissions Trend — Scope 1 + 2 + 3</div>
                <div className="text-xs text-ink-soft mt-0.5">Sum across all included tenants per period.</div>
              </div>
              <div className="flex gap-2 text-[11px]">
                <span className="flex items-center gap-1"><span className="dot" style={{background:'#064E3B'}}/> S1</span>
                <span className="flex items-center gap-1"><span className="dot" style={{background:'#10B981'}}/> S2</span>
                <span className="flex items-center gap-1"><span className="dot" style={{background:'#A7F3D0'}}/> S3</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 border-t-2 border-status-error"/> Total</span>
              </div>
            </div>
            <div className="h-[260px] mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.emissions_trend} barCategoryGap={14}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false}/>
                  <XAxis dataKey="period" tick={{fontSize: 11, fill: '#64748B'}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize: 11, fill: '#64748B'}} axisLine={false} tickLine={false} width={60}/>
                  <Tooltip />
                  <Bar dataKey="scope1" stackId="a" fill="#064E3B" radius={[2,2,0,0]}/>
                  <Bar dataKey="scope2" stackId="a" fill="#10B981"/>
                  <Bar dataKey="scope3" stackId="a" fill="#A7F3D0" radius={[0,0,2,2]}/>
                  <Line type="monotone" dataKey="total" stroke="#EF4444" strokeWidth={2.5} dot={{r:3}} name="Total S1+2+3"/>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* KPI rollup */}
          <div className="card overflow-hidden" data-testid="kpi-rollup">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <div className="section-label">Consolidated KPI rollup ({data.rows.length} KPIs)</div>
                <div className="text-xs text-ink-soft mt-0.5">Click a row to see per-tenant breakdown.</div>
              </div>
              <span className="text-[11px] text-ink-soft flex items-center gap-1">
                <Info size={11}/> {mode === "interim" ? "Interim — may include partial submissions." : "Actual — strict completeness gate."}
              </span>
            </div>
            <table className="dense">
              <thead><tr>
                <th></th>
                <th>KPI</th>
                <th>Material issue</th>
                <th>Unit</th>
                <th className="text-right">Total</th>
                <th className="text-right">Average</th>
                <th className="text-right">Min</th>
                <th className="text-right">Max</th>
                <th className="text-right">Tenants</th>
                <th className="text-right">Avg conf.</th>
              </tr></thead>
              <tbody>
                {Object.entries(grouped).map(([issue, kpis]) => (
                  <React.Fragment key={issue}>
                    <tr><td colSpan={10} className="bg-surface/60 text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 text-ink-soft">{issue}</td></tr>
                    {kpis.map((r) => {
                      const open = expandedKpi === r.kpi.id;
                      return (
                        <React.Fragment key={r.kpi.id}>
                          <tr data-testid={`row-${r.kpi.id}`} onClick={() => setExpandedKpi(open ? null : r.kpi.id)} className="cursor-pointer hover:bg-surface/60">
                            <td>{open ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}</td>
                            <td className="font-medium">{r.kpi.name}</td>
                            <td className="text-[11px] text-ink-soft">{r.kpi.material_issue}</td>
                            <td className="text-[11px] font-mono">{r.kpi.unit}</td>
                            <td className="num font-semibold">{fmtNumber(r.total)}</td>
                            <td className="num">{fmtNumber(r.average)}</td>
                            <td className="num text-ink-soft">{fmtNumber(r.min)}</td>
                            <td className="num text-ink-soft">{fmtNumber(r.max)}</td>
                            <td className="num">{r.tenants_reporting}</td>
                            <td className="num">{(r.avg_confidence * 100).toFixed(0)}%</td>
                          </tr>
                          {open && (
                            <tr><td colSpan={10} className="bg-surface/40 p-3">
                              <div className="dlabel mb-2">Per-tenant breakdown · {r.kpi.name}</div>
                              <table className="dense w-full">
                                <thead><tr><th>Tenant</th><th className="text-right">Value</th><th className="text-right">Confidence</th></tr></thead>
                                <tbody>
                                  {r.breakdown.map((b) => (
                                    <tr key={b.tenant_id}>
                                      <td>{b.tenant_name}</td>
                                      <td className="num">{fmtNumber(b.value)} {r.kpi.unit}</td>
                                      <td className="num">{(b.confidence * 100).toFixed(0)}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
                {data.rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-ink-soft py-6 text-xs">No KPIs aggregated in this mode for {data.period}.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Report modal */}
      {report && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setReport(null)} data-testid="report-modal">
          <div className="card w-full max-w-4xl bg-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-white">
              <div>
                <div className="section-label">Generated</div>
                <div className="font-semibold mt-0.5">{report.title}</div>
                <div className="text-xs text-ink-soft">{report.mode.toUpperCase()} · Period {report.period} · by {report.generated_by}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid="download-report" onClick={() => downloadReport(report)} className="btn btn-primary"><Download size={12}/> Download .md</button>
                <Link to="/platform/health" className="btn btn-secondary">View tenants health</Link>
                <button onClick={() => setReport(null)} className="btn btn-ghost">Close</button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              {report.sections.map((s) => (
                <div key={s.title}>
                  <div className="section-label">{s.title}</div>
                  <pre className="text-sm whitespace-pre-wrap font-sans mt-1">{s.body}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, sub, color }) {
  return (
    <div className="card p-4" data-testid={`tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2"><span className="dot" style={{background: color}}/><span className="section-label">{label}</span></div>
      <div className="metric-value mt-1">{value}</div>
      <div className="text-[11px] text-ink-soft">{sub}</div>
    </div>
  );
}
